/**
 * QUALITY-NCR-REVIEW (Aaron, 29 Sep 2026): two per-customer NCR choices.
 *
 * - E4: what the customer calls its own number for an NCR (DMR No., SRN No.,
 *   QN No. ...). Labels that box on the NCR form, the register column, the
 *   export and the print. Stored as `ncr_customer_reference_label`.
 * - E5: whether suppliers see the grades their 8D was given. Default OFF, as
 *   legacy never showed them. Stored in `feature_flags.ncr_share_8d_scores_with_supplier`
 *   and enforced by the server, which blanks the grades for a supplier viewer.
 *
 * A Helix operator writes the platform default; a client admin's save goes to
 * their own tenant's override (`core.admin_setup_settings`).
 */
import { useState, useEffect } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Button } from '../../ui/button';
import { Loader2, Hash } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_LABEL = 'Customer NCR No.';
const SHARE_FLAG = 'ncr_share_8d_scores_with_supplier' as const;

export function NCRNumberAndGradesSection() {
  const [loading, setLoading] = useState(true);
  // Round-1 review: a failed load must not leave live controls on defaults --
  // a Helix operator's toggle would then save a near-empty feature_flags.
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [label, setLabel] = useState(DEFAULT_LABEL);
  const [savedLabel, setSavedLabel] = useState(DEFAULT_LABEL);
  const [share, setShare] = useState(false);
  const [savingShare, setSavingShare] = useState(false);
  // The full feature_flags dict is kept so a toggle save doesn't drop siblings.
  const [featureFlags, setFeatureFlags] = useState<Record<string, unknown>>({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const { data } = await platformSettingsAPI.getSettings();
        const current = data.ncr_customer_reference_label || DEFAULT_LABEL;
        setLabel(current);
        setSavedLabel(current);
        const flags = data.feature_flags ?? {};
        setFeatureFlags(flags);
        setShare(!!flags[SHARE_FLAG]);
      } catch (err: unknown) {
        console.error('Error loading NCR number and grade settings:', err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [attempt]);

  const saveLabel = async () => {
    const next = label.trim() || DEFAULT_LABEL;
    setLabel(next);
    if (next === savedLabel) return;
    try {
      await platformSettingsAPI.patchSettings({ ncr_customer_reference_label: next });
      setSavedLabel(next);
      toast.success(`Your NCR number is now called "${next}"`);
    } catch (err: unknown) {
      console.error('Error saving the NCR number name:', err);
      toast.error(getApiErrorMessage(err, 'Failed to save the name for your NCR number'));
      setLabel(savedLabel);
    }
  };

  const toggleShare = async (checked: boolean) => {
    const previous = share;
    setShare(checked);
    setSavingShare(true);
    try {
      const nextFlags = { ...featureFlags, [SHARE_FLAG]: checked };
      await platformSettingsAPI.patchSettings({ feature_flags: nextFlags });
      setFeatureFlags(nextFlags);
      toast.success(checked ? 'Suppliers now see their 8D grades' : 'Suppliers no longer see their 8D grades');
    } catch (err: unknown) {
      console.error('Error saving the 8D grade sharing setting:', err);
      toast.error(getApiErrorMessage(err, 'Failed to save the 8D grade sharing setting'));
      setShare(previous);
    } finally {
      setSavingShare(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5" />
            NCR number and 8D grades
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <p className="text-sm text-red-600">These settings couldn't be loaded, so they can't be changed right now.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => setAttempt((a) => a + 1)}>Try again</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Hash className="h-5 w-5" />
          NCR number and 8D grades
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ncr-customer-reference-label">What you call your own NCR number</Label>
          <Input
            id="ncr-customer-reference-label"
            className="max-w-xs"
            maxLength={40}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={saveLabel}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          />
          <p className="text-xs text-muted-foreground">
            For example DMR No. or SRN No. It names that box on the NCR form, the register column, the
            export and the printed NCR. Helix still gives every NCR its own NCR number.
          </p>
        </div>
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="space-y-1 pr-3">
            <Label htmlFor="ncr-share-8d-scores" className="text-sm font-medium">
              Show suppliers their 8D grades
            </Label>
            <p className="text-xs text-muted-foreground">
              {share
                ? 'On: a supplier sees the grades you gave its 8D and the 8D score, on the register and the NCR.'
                : 'Off: only your team sees the grades. Suppliers see blank grade columns.'}
            </p>
          </div>
          <Switch
            id="ncr-share-8d-scores"
            checked={share}
            onCheckedChange={toggleShare}
            disabled={savingShare}
          />
        </div>
      </CardContent>
    </Card>
  );
}
